import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { getEmail, clearSession } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import { KeyRound, User, Save, LogOut, Mail, Calendar as CalIcon, RefreshCcw, Copy, Smartphone, Globe, ExternalLink, Users, Trash2, Plus } from "lucide-react";
import { errMsg } from "@/lib/errors";

export default function ProfileTab() {
  const navigate = useNavigate();
  const email = getEmail();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState(email || "");
  const [testSending, setTestSending] = useState(false);
  const [feedUrl, setFeedUrl] = useState(null);
  const [feedConfigured, setFeedConfigured] = useState(false);
  const [feedRotatedAt, setFeedRotatedAt] = useState(null);
  const [feedBusy, setFeedBusy] = useState(false);

  // iter219 — Rubrica Collaboratori Pulizie
  const [collaborators, setCollaborators] = useState([]);
  const [collabNew, setCollabNew] = useState({ name: "", phone: "" });
  const [collabBusy, setCollabBusy] = useState(false);
  const loadCollaborators = async () => {
    try {
      const { data } = await api.get("/admin/cleaning-collaborators");
      setCollaborators(data?.items || []);
    } catch { /* noop */ }
  };
  const addCollaborator = async () => {
    if (!collabNew.name.trim() || !collabNew.phone.trim()) {
      toast.error("Nome e telefono sono obbligatori");
      return;
    }
    setCollabBusy(true);
    try {
      await api.post("/admin/cleaning-collaborators", { name: collabNew.name, phone: collabNew.phone, active: true });
      setCollabNew({ name: "", phone: "" });
      await loadCollaborators();
      toast.success("Collaboratrice aggiunta");
    } catch (e) {
      toast.error(errMsg(e) || "Errore aggiunta");
    } finally {
      setCollabBusy(false);
    }
  };
  const toggleCollaborator = async (c) => {
    try {
      await api.patch(`/admin/cleaning-collaborators/${c.id}`, { name: c.name, phone: c.phone, active: !c.active });
      await loadCollaborators();
    } catch (e) {
      toast.error(errMsg(e) || "Errore aggiornamento");
    }
  };
  const deleteCollaborator = async (c) => {
    if (!window.confirm(`Rimuovere ${c.name} dalla rubrica?`)) return;
    try {
      await api.delete(`/admin/cleaning-collaborators/${c.id}`);
      await loadCollaborators();
      toast.success("Rimossa");
    } catch (e) {
      toast.error(errMsg(e) || "Errore");
    }
  };
  useEffect(() => { loadCollaborators(); }, []);

  // iter208 — Account OTA (URL + username, password mai salvate)
  const [ota, setOta] = useState({
    booking_url: "", booking_email: "",
    airbnb_url: "", airbnb_email: "",
    notes: "",
  });
  const [otaSaving, setOtaSaving] = useState(false);
  const [otaLoaded, setOtaLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/ota-accounts");
        setOta({
          booking_url: r.data.booking_url || "",
          booking_email: r.data.booking_email || "",
          airbnb_url: r.data.airbnb_url || "",
          airbnb_email: r.data.airbnb_email || "",
          notes: r.data.notes || "",
        });
      } finally { setOtaLoaded(true); }
    })();
  }, []);

  const saveOtaAccounts = async () => {
    setOtaSaving(true);
    try {
      await api.put("/admin/ota-accounts", ota);
      toast.success("Account OTA salvati");
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    } finally { setOtaSaving(false); }
  };

  // === Google Calendar OAuth (push immediato) ===
  // Stato della connessione OAuth con il Google Calendar dell'host. Se
  // connesso, ogni booking nuova viene creata in tempo reale come evento.
  const [gcalStatus, setGcalStatus] = useState({ connected: false, configured: false });
  const [gcalBusy, setGcalBusy] = useState(false);

  const loadGcalStatus = async () => {
    try {
      const r = await api.get("/admin/google-calendar/status");
      setGcalStatus(r.data || { connected: false, configured: false });
    } catch { /* ignore */ }
  };

  useEffect(() => { loadGcalStatus(); }, []);

  const connectGcal = async () => {
    setGcalBusy(true);
    try {
      const r = await api.get("/admin/google-calendar/auth-url");
      const url = r.data?.auth_url;
      if (!url) { toast.error("Errore generazione link Google"); return; }
      // Apro la finestra di consenso Google. Quando l'utente conferma,
      // Google reindirizza a /api/oauth/google-calendar/callback che salva
      // i token e mostra una pagina di conferma con auto-close.
      const popup = window.open(url, "google-oauth", "width=520,height=640");
      // Polling ogni 2 secondi per rilevare quando la finestra si chiude
      // (= autorizzazione completata o annullata).
      const timer = setInterval(async () => {
        if (popup && popup.closed) {
          clearInterval(timer);
          await loadGcalStatus();
          setGcalBusy(false);
        }
      }, 2000);
    } catch (e) {
      toast.error(errMsg(e, "Errore connessione Google"));
      setGcalBusy(false);
    }
  };

  const disconnectGcal = async () => {
    setGcalBusy(true);
    try {
      await api.post("/admin/google-calendar/disconnect");
      toast.success("Google Calendar disconnesso");
      await loadGcalStatus();
    } catch {
      toast.error("Errore disconnessione");
    } finally { setGcalBusy(false); }
  };

  const syncAllGcal = async () => {
    setGcalBusy(true);
    try {
      const r = await api.post("/admin/google-calendar/sync-all");
      const { pushed = 0, failed = 0 } = r.data || {};
      toast.success(`Sincronizzate ${pushed} prenotazioni${failed ? ` (${failed} errori)` : ""}`);
      await loadGcalStatus();
    } catch (e) {
      toast.error(errMsg(e, "Errore sincronizzazione"));
    } finally { setGcalBusy(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/calendar-feed/info");
        if (r.data?.configured) {
          setFeedUrl(r.data.url);
          setFeedConfigured(true);
          setFeedRotatedAt(r.data.rotated_at);
        }
      } catch { /* ignore */ }
    })();
  }, []);

  const generateFeed = async () => {
    setFeedBusy(true);
    try {
      const r = await api.post("/admin/calendar-feed/rotate-token");
      let url = r.data.url || "";
      // If backend couldn't resolve its public URL, prefix with the current origin
      if (url.startsWith("/api/")) {
        const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        url = `${backend.replace(/\/$/, "")}${url}`;
      }
      setFeedUrl(url);
      setFeedConfigured(true);
      setFeedRotatedAt(new Date().toISOString());
      toast.success(feedConfigured ? "Nuovo link generato (il vecchio non funziona più)" : "Link feed creato — copialo e incollalo in Google Calendar");
    } catch (err) {
      toast.error(errMsg(err, "Errore"));
    } finally {
      setFeedBusy(false);
    }
  };

  const copyFeed = async () => {
    if (!feedUrl) return;
    const ok = await copyToClipboard(feedUrl);
    if (ok) toast.success("Link copiato negli appunti");
    else toast.error("Copia non riuscita, seleziona manualmente");
  };

  const installPwaHint = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      toast.info("Su iPhone/iPad: apri questa app in Safari → Condividi → 'Aggiungi a Home'", { duration: 8000 });
    } else {
      toast.info("Su Android (Chrome): apri il menù ⋮ → 'Installa app' o 'Aggiungi a schermata Home'", { duration: 8000 });
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail || !testEmail.includes("@")) return toast.error("Email non valida");
    setTestSending(true);
    try {
      const r = await api.post(`/admin/email-test?to=${encodeURIComponent(testEmail)}`);
      if (r.data?.ok) toast.success(`Email di test inviata a ${testEmail}. Controlla anche Spam/Promozioni!`);
      else toast.error("SMTP non configurato o invio fallito.");
    } catch (err) {
      toast.error(errMsg(err, "Errore"));
    } finally {
      setTestSending(false);
    }
  };

  const submit = async () => {
    if (!form.current_password) return toast.error("Inserisci la password attuale");
    if (form.new_password.length < 8) return toast.error("La nuova password deve avere almeno 8 caratteri");
    if (form.new_password !== form.confirm) return toast.error("Le due nuove password non corrispondono");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password aggiornata. Effettua di nuovo il login.");
      clearSession();
      setTimeout(() => navigate("/admin/login"), 1200);
    } catch (err) {
      toast.error(errMsg(err, "Errore"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="profile-tab" className="space-y-5 max-w-xl">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2 mb-4">
          <User className="w-5 h-5" /> Profilo Admin
        </h3>
        <div className="text-sm text-[#635F59] space-y-1">
          <div className="flex justify-between"><span>Email</span><span className="font-medium text-[#1E3A5F]">{email}</span></div>
        </div>
      </div>

      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2 mb-4">
          <KeyRound className="w-5 h-5" /> Cambio password
        </h3>
        <p className="text-xs text-[#635F59] mb-4">Per sicurezza, dopo il cambio dovrai effettuare nuovamente il login.</p>
        <div className="space-y-3">
          <div>
            <Label>Password attuale</Label>
            <Input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="mt-1.5" data-testid="pwd-current" autoComplete="current-password" />
          </div>
          <div>
            <Label>Nuova password (min. 8 caratteri)</Label>
            <Input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-1.5" data-testid="pwd-new" autoComplete="new-password" />
          </div>
          <div>
            <Label>Conferma nuova password</Label>
            <Input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1.5" data-testid="pwd-confirm" autoComplete="new-password" />
          </div>
          <Button onClick={submit} disabled={saving} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="pwd-save">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio..." : "Aggiorna password"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2 mb-3">
          <Mail className="w-5 h-5" /> Test email
        </h3>
        <p className="text-xs text-[#635F59] mb-3">
          Invia un'email di prova per verificare che il sistema email stia funzionando. Se non arriva, controlla la cartella <strong>Spam</strong> o <strong>Promozioni</strong>.
        </p>
        <div className="flex gap-2 flex-wrap">
          <Input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="email destinatario"
            className="max-w-sm"
            data-testid="test-email-input"
          />
          <Button onClick={sendTestEmail} disabled={testSending} className="bg-[#1E3A5F] hover:bg-[#152A45] text-white" data-testid="test-email-send">
            <Mail className="w-4 h-4 mr-2" /> {testSending ? "Invio..." : "Invia test"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2 mb-3">
          <Smartphone className="w-5 h-5" /> Widget e Calendario su cellulare
        </h3>
        <p className="text-xs text-[#635F59] mb-3">
          Aggiungi un'icona sulla home del telefono che apre direttamente la tua agenda <b>Oggi</b>:
          check-in, check-out, ospiti in casa e navette. Funziona offline.
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-5">
          <div className="border border-[#E8E2D9] rounded-lg p-3 text-xs space-y-2">
            <div className="font-semibold text-[#1E3A5F]">1) Apri il widget</div>
            <p className="text-[#635F59]">
              Apri questo link dal cellulare:<br />
              <code className="bg-[#FDFBF7] px-1 py-0.5 rounded text-[10px]">/admin/widget</code>
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/admin/widget")}
              data-testid="open-widget"
            >
              Apri ora
            </Button>
          </div>
          <div className="border border-[#E8E2D9] rounded-lg p-3 text-xs space-y-2">
            <div className="font-semibold text-[#1E3A5F]">2) Aggiungi alla home</div>
            <p className="text-[#635F59]">
              Dal browser del telefono → menu Condividi → <b>"Aggiungi a Home"</b> (iOS) o
              <b> "Installa app"</b> (Android).
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={installPwaHint}
              data-testid="install-hint"
            >
              Come fare?
            </Button>
          </div>
        </div>

        <h4 className="font-display text-base text-[#1E3A5F] flex items-center gap-2 mb-2 pt-4 border-t border-[#E8E2D9]">
          <CalIcon className="w-4 h-4" /> Push immediato Google Calendar
          {gcalStatus.connected && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">CONNESSO</span>}
        </h4>
        <p className="text-xs text-[#635F59] mb-3">
          Push <b>in tempo reale</b> di ogni nuova prenotazione sul tuo Google Calendar. Risolve il
          ritardo (12-24h) della sottoscrizione iCal classica. Richiede una autorizzazione una tantum.
        </p>

        {!gcalStatus.configured && (
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-xs text-amber-900" data-testid="gcal-not-configured">
            ⚠ Google OAuth non configurato sul backend. Controlla che <code className="bg-white px-1 rounded">GOOGLE_OAUTH_CLIENT_ID</code> e <code className="bg-white px-1 rounded">GOOGLE_OAUTH_CLIENT_SECRET</code> siano nel <code className="bg-white px-1 rounded">.env</code>.
          </div>
        )}

        {gcalStatus.configured && !gcalStatus.connected && (
          <Button
            onClick={connectGcal}
            disabled={gcalBusy}
            className="bg-[#1E3A5F] hover:bg-[#152A45] text-white"
            data-testid="gcal-connect"
          >
            <CalIcon className="w-4 h-4 mr-2" /> {gcalBusy ? "Apertura Google..." : "Connetti Google Calendar"}
          </Button>
        )}

        {gcalStatus.configured && gcalStatus.connected && (
          <div className="space-y-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs space-y-1" data-testid="gcal-connected-info">
              <div><b>Account:</b> {gcalStatus.calendar_email || "—"}</div>
              {gcalStatus.connected_at && (
                <div className="text-[#635F59]">
                  Connesso il {new Date(gcalStatus.connected_at).toLocaleString("it-IT")}
                </div>
              )}
              {gcalStatus.last_sync_at && (
                <div className="text-[#635F59]">
                  Ultima sync: {new Date(gcalStatus.last_sync_at).toLocaleString("it-IT")}
                </div>
              )}
              {gcalStatus.last_error && (
                <div className="text-rose-700">⚠ Ultimo errore: {gcalStatus.last_error}</div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                onClick={syncAllGcal}
                disabled={gcalBusy}
                variant="outline"
                className="border-[#1E3A5F] text-[#1E3A5F]"
                data-testid="gcal-sync-all"
              >
                <RefreshCcw className="w-3.5 h-3.5 mr-1" /> {gcalBusy ? "Sincronizzo..." : "Sincronizza tutte le prenotazioni esistenti"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={disconnectGcal}
                disabled={gcalBusy}
                className="text-rose-600 hover:bg-rose-50"
                data-testid="gcal-disconnect"
              >
                Disconnetti
              </Button>
            </div>
          </div>
        )}

        <h4 className="font-display text-base text-[#1E3A5F] flex items-center gap-2 mb-2 pt-4 border-t border-[#E8E2D9]">
          <CalIcon className="w-4 h-4" /> Link iCal (alternativa, refresh lento)
        </h4>
        <p className="text-xs text-[#635F59] mb-3">
          Link iCal pubblico-con-token che importa <b>automaticamente</b> tutte le prenotazioni nel tuo Google Calendar.
          Refresh ogni 12-24h (limitazione Google). Per il push in tempo reale usa la sezione sopra.
        </p>

        {!feedConfigured && (
          <Button
            onClick={generateFeed}
            disabled={feedBusy}
            className="bg-[#1E3A5F] hover:bg-[#152A45] text-white"
            data-testid="feed-generate"
          >
            <CalIcon className="w-4 h-4 mr-2" /> {feedBusy ? "Creazione..." : "Crea link Google Calendar"}
          </Button>
        )}

        {feedConfigured && (
          <div className="space-y-2">
            <Label className="text-xs">Link da incollare in Google Calendar</Label>
            <div className="flex gap-2">
              <Input
                value={feedUrl || ""}
                readOnly
                className="font-mono text-[11px]"
                onFocus={(e) => e.target.select()}
                data-testid="feed-url"
              />
              <Button onClick={copyFeed} variant="outline" size="sm" data-testid="feed-copy">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-[#635F59]">
              Su <b>calendar.google.com</b> → <i>Altri calendari</i> ➕ → <i>"Da URL"</i> → incolla il link sopra.
              {feedRotatedAt && ` · Ultimo cambio token: ${new Date(feedRotatedAt).toLocaleString("it-IT")}`}
            </p>
            <Button
              size="sm"
              variant="ghost"
              onClick={generateFeed}
              disabled={feedBusy}
              className="text-[#AC5D45] hover:bg-[#FDFBF7]"
              data-testid="feed-rotate"
            >
              <RefreshCcw className="w-3 h-3 mr-1" /> Rigenera (invalida il link precedente)
            </Button>
          </div>
        )}
      </div>

      {/* iter208 — Account OTA (link personalizzati) */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6" data-testid="ota-accounts-card">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2 mb-1">
          <Globe className="w-5 h-5" /> Account OTA
        </h3>
        <p className="text-xs text-[#635F59] mb-4">
          URL personalizzati e username per Booking.com e Airbnb. Le password <b>non vengono salvate qui</b> — restano nel password manager del tuo browser (Chrome/Safari). Quando cambi login OTA, aggiorna solo l&apos;URL/username qui: i pulsanti &quot;Vai su Booking&quot; e &quot;Vai su Airbnb&quot; useranno automaticamente queste destinazioni.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-[#1E3A5F] flex items-center gap-1.5">
              <span className="inline-block w-4 h-4 rounded-sm bg-[#003580] shrink-0" /> Booking.com
            </Label>
            <div>
              <Label className="text-xs text-[#635F59]">URL Extranet</Label>
              <Input
                value={ota.booking_url}
                onChange={(e) => setOta({ ...ota, booking_url: e.target.value })}
                placeholder="https://admin.booking.com/"
                data-testid="ota-booking-url"
              />
            </div>
            <div>
              <Label className="text-xs text-[#635F59]">Email / Username</Label>
              <Input
                value={ota.booking_email}
                onChange={(e) => setOta({ ...ota, booking_email: e.target.value })}
                placeholder="tuo@email.it"
                data-testid="ota-booking-email"
              />
            </div>
            {ota.booking_url && (
              <a
                href={ota.booking_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#003580] hover:underline"
                data-testid="ota-booking-test"
              >
                <ExternalLink className="w-3 h-3" /> Prova il link
              </a>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-[#1E3A5F] flex items-center gap-1.5">
              <span className="inline-block w-4 h-4 rounded-sm bg-[#FF5A5F] shrink-0" /> Airbnb
            </Label>
            <div>
              <Label className="text-xs text-[#635F59]">URL Host</Label>
              <Input
                value={ota.airbnb_url}
                onChange={(e) => setOta({ ...ota, airbnb_url: e.target.value })}
                placeholder="https://www.airbnb.it/hosting"
                data-testid="ota-airbnb-url"
              />
            </div>
            <div>
              <Label className="text-xs text-[#635F59]">Email / Username</Label>
              <Input
                value={ota.airbnb_email}
                onChange={(e) => setOta({ ...ota, airbnb_email: e.target.value })}
                placeholder="tuo@email.it"
                data-testid="ota-airbnb-email"
              />
            </div>
            {ota.airbnb_url && (
              <a
                href={ota.airbnb_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[#FF5A5F] hover:underline"
                data-testid="ota-airbnb-test"
              >
                <ExternalLink className="w-3 h-3" /> Prova il link
              </a>
            )}
          </div>
        </div>
        <div className="mt-4">
          <Label className="text-xs text-[#635F59]">Note libere (facoltativo)</Label>
          <Input
            value={ota.notes}
            onChange={(e) => setOta({ ...ota, notes: e.target.value })}
            placeholder="Es. account Booking.com condiviso con moglie · Airbnb Superhost"
            data-testid="ota-notes"
          />
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={saveOtaAccounts}
            disabled={!otaLoaded || otaSaving}
            className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
            data-testid="ota-save"
          >
            <Save className="w-4 h-4 mr-2" /> {otaSaving ? "Salvo…" : "Salva Account OTA"}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6" data-testid="cleaning-collaborators-card">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2 mb-1">
          <Users className="w-5 h-5" /> Rubrica Collaboratrici Pulizie
        </h3>
        <p className="text-xs text-[#635F59] mb-4">
          Aggiungi qui le persone a cui invii il piano pulizie. Il numero serve per creare il messaggio WhatsApp con il link condivisibile del piano di domani.
        </p>
        {/* Lista */}
        {collaborators.length === 0 ? (
          <div className="text-sm text-stone-500 italic mb-4">Nessuna collaboratrice ancora in rubrica.</div>
        ) : (
          <div className="space-y-2 mb-4">
            {collaborators.map(c => (
              <div key={c.id} className="flex items-center gap-3 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2" data-testid={`collab-row-${c.id}`}>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm ${c.active ? "text-[#1E3A5F]" : "text-stone-400 line-through"}`}>{c.name}</div>
                  <div className="text-xs text-[#635F59]">{c.phone}</div>
                </div>
                <label className="text-xs flex items-center gap-1 cursor-pointer select-none">
                  <input type="checkbox" checked={c.active} onChange={() => toggleCollaborator(c)} className="w-4 h-4 accent-[#AC5D45]" />
                  Attiva
                </label>
                <button
                  onClick={() => deleteCollaborator(c)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title="Rimuovi"
                  data-testid={`collab-delete-${c.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
        {/* Aggiungi nuova */}
        <div className="border-t border-stone-200 pt-3">
          <div className="text-xs font-bold text-stone-700 mb-2">Aggiungi nuova collaboratrice:</div>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Nome (es. Rosa)"
              value={collabNew.name}
              onChange={(e) => setCollabNew({ ...collabNew, name: e.target.value })}
              className="flex-1 min-w-[150px]"
              data-testid="collab-new-name"
            />
            <Input
              placeholder="Telefono (+39 349...)"
              value={collabNew.phone}
              onChange={(e) => setCollabNew({ ...collabNew, phone: e.target.value })}
              className="flex-1 min-w-[150px]"
              data-testid="collab-new-phone"
            />
            <Button
              onClick={addCollaborator}
              disabled={collabBusy || !collabNew.name.trim() || !collabNew.phone.trim()}
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
              data-testid="collab-add"
            >
              <Plus className="w-4 h-4 mr-1" /> Aggiungi
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6">
        <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2 mb-3">
          <LogOut className="w-5 h-5" /> Sessione
        </h3>
        <p className="text-xs text-[#635F59] mb-3">Termina la sessione corrente su questo dispositivo.</p>
        <Button variant="outline" onClick={() => { clearSession(); navigate("/admin/login", { replace: true }); }} data-testid="profile-logout">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </div>
    </section>
  );
}
